import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Slider } from '@/components/ui/slider'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { useMediaFolders, useCreateFolder, useUploadToFolder } from '@/api/hooks/useMediaLibrary'
import { useVideoCut, cutWithNsfwSSE } from '@/api/hooks/useVideoCut'
import type { CutSSEEvent } from '@/api/hooks/useVideoCut'
import type { AiLog, MediaFolder } from '@/types'
import { AiLogDetail } from '@/components/AiLogDetail'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  Scissors, Loader2, CheckCircle2, Video, FolderOpen, Folder, Plus, Check, X, Upload, CalendarPlus, AlertTriangle, ChevronRight, Play, Eye, ChevronDown, Home,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { QuickScheduleDialog } from './QuickScheduleDialog'

interface BulkResult {
  totalVideos: number
  totalClips: number
  failed: string[]
  folders: Array<{ id: string; name: string; clipCount: number }>
}

export function VideoCutTab() {
  const { t } = useLanguage()
  const { data: folders } = useMediaFolders()
  const createFolder = useCreateFolder()
  const { uploadProgress, fileCount, ...uploadToFolder } = useUploadToFolder()
  const videoCut = useVideoCut()
  const uploadRef = useRef<HTMLInputElement>(null)

  // Navigation
  const [currentFolderId, setCurrentFolderId] = useState<string>('')
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(new Set())

  // Cut config
  const [maxClips, setMaxClips] = useState(5)
  const [reelsMode, setReelsMode] = useState(true)
  const [maxDuration, setMaxDuration] = useState(90)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [nsfwReview, setNsfwReview] = useState(false)
  const [outputQuality, setOutputQuality] = useState<'720p' | '1080p'>('720p')
  const [aiSensitivity, setAiSensitivity] = useState<'low' | 'medium' | 'high'>('medium')
  const [blurIntensity, setBlurIntensity] = useState<'light' | 'medium' | 'strong'>('medium')
  const [clipModel, setClipModel] = useState<'openclip' | 'dinov2'>('openclip')

  // Bulk cut state
  const [bulkCutting, setBulkCutting] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentName: '' })
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null)

  // NSFW review SSE progress
  const [aiStage, setAiStage] = useState<{ clip: number; total: number; stage: string; message: string } | null>(null)
  const [aiStats, setAiStats] = useState({ blurred: 0, clean: 0 })

  // Custom video names for clips
  const [videoNames, setVideoNames] = useState<Record<string, string>>({})

  // Video preview
  const [playingId, setPlayingId] = useState<string | null>(null)

  // AI log per clip
  const [clipLogs, setClipLogs] = useState<Array<{
    clip: number
    filename: string
    url: string
    nsfwDetected: boolean
    aiLog?: AiLog
  }>>([])
  const [expandedLog, setExpandedLog] = useState<number | null>(null)

  // === Derived data ===

  const currentFolder = folders?.find(f => f.id === currentFolderId) || null

  const subfolders = useMemo(() => {
    if (!folders) return []
    return folders
      .filter(f => currentFolderId ? f.parentId === currentFolderId : !f.parentId)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [folders, currentFolderId])

  const currentVideos = useMemo(() => {
    if (!currentFolder) return []
    return currentFolder.images.filter(i => i.mediaType === 'video' && !i.clip)
  }, [currentFolder])

  // Count videos recursively for folder badges
  const countVideosRecursive = useCallback((folderId: string): number => {
    if (!folders) return 0
    const folder = folders.find(f => f.id === folderId)
    if (!folder) return 0
    const direct = folder.images.filter(i => i.mediaType === 'video' && !i.clip).length
    const children = folders.filter(f => f.parentId === folderId)
    return direct + children.reduce((sum, c) => sum + countVideosRecursive(c.id), 0)
  }, [folders])

  // Map video id → folder id
  const videoFolderMap = useMemo(() => {
    const map: Record<string, string> = {}
    folders?.forEach(f => { f.images.forEach(img => { map[img.id] = f.id }) })
    return map
  }, [folders])

  // Breadcrumb path
  const breadcrumb = useMemo(() => {
    if (!folders || !currentFolderId) return []
    const path: Array<{ id: string; name: string }> = []
    let id: string | null | undefined = currentFolderId
    while (id) {
      const folder = folders.find(f => f.id === id)
      if (!folder) break
      path.unshift({ id: folder.id, name: folder.name })
      id = folder.parentId
    }
    return path
  }, [folders, currentFolderId])

  // Selected videos resolved
  const selectedVideos = useMemo(() => {
    if (!folders || selectedVideoIds.size === 0) return []
    const result: Array<{ id: string; url: string; filename: string; folderId: string }> = []
    folders.forEach(f => {
      f.images.forEach(img => {
        if (selectedVideoIds.has(img.id) && img.mediaType === 'video' && !img.clip) {
          result.push({ id: img.id, url: img.url, filename: img.filename, folderId: f.id })
        }
      })
    })
    return result
  }, [folders, selectedVideoIds])

  // Reset selections on navigation
  useEffect(() => {
    setBulkResult(null)
    setPlayingId(null)
  }, [currentFolderId])

  // === Handlers ===

  const navigateTo = (folderId: string) => {
    setCurrentFolderId(folderId)
  }

  const navigateUp = () => {
    if (currentFolder?.parentId) setCurrentFolderId(currentFolder.parentId)
    else setCurrentFolderId('')
  }

  const toggleVideo = (videoId: string) => {
    setSelectedVideoIds(prev => {
      const next = new Set(prev)
      if (next.has(videoId)) next.delete(videoId)
      else next.add(videoId)
      return next
    })
  }

  const toggleAllCurrentVideos = () => {
    const allIds = currentVideos.map(v => v.id)
    const allSelected = allIds.every(id => selectedVideoIds.has(id))
    setSelectedVideoIds(prev => {
      const next = new Set(prev)
      if (allSelected) {
        allIds.forEach(id => next.delete(id))
      } else {
        allIds.forEach(id => next.add(id))
      }
      return next
    })
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return
    createFolder.mutate({ name: newFolderName.trim(), parentId: currentFolderId || undefined }, {
      onSuccess: (data) => {
        toast.success(t('Pasta criada!'))
        setNewFolderName('')
        setShowCreateFolder(false)
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleUpload = (files: FileList) => {
    if (!currentFolderId) { toast.error(t('Entre em uma pasta primeiro')); return }
    uploadToFolder.mutate({ folderId: currentFolderId, files: Array.from(files) }, {
      onSuccess: (data: { added: number; failed?: string[]; failedCount?: number }) => {
        if (data.failedCount && data.failedCount > 0) {
          toast.warning(`${data.added} enviado(s), ${data.failedCount} falharam`, { description: data.failed?.join('\n'), duration: 8000 })
        } else {
          toast.success(`${data.added} video(s) enviado(s)!`)
        }
      },
      onError: (err) => toast.error(err.message, { duration: 6000 }),
    })
  }

  const handleBulkCut = async () => {
    if (selectedVideos.length === 0) return
    if (!confirm(`Cortar ${selectedVideos.length} video(s)${nsfwReview ? ' com revisao de conteudo' : ''}? Isso pode levar varios minutos.`)) return

    setBulkCutting(true)
    setBulkResult(null)
    setAiStage(null)
    setAiStats({ blurred: 0, clean: 0 })
    setClipLogs([])
    setExpandedLog(null)
    setBulkProgress({ current: 0, total: selectedVideos.length, currentName: '' })

    let totalClips = 0
    const failed: string[] = []
    const resultFolders: BulkResult['folders'] = []

    for (let i = 0; i < selectedVideos.length; i++) {
      const video = selectedVideos[i]
      const clipTitle = videoNames[video.id]?.trim() || video.filename.replace(/\.[^.]+$/, '')
      const folderId = video.folderId
      setBulkProgress({ current: i + 1, total: selectedVideos.length, currentName: clipTitle })

      try {
        if (nsfwReview) {
          const data = await cutWithNsfwSSE(
            { videoUrl: video.url, maxClips, maxDuration, folderId, clipTitle, reelsMode, nsfwReview, outputQuality, aiSensitivity, blurIntensity, clipModel },
            (event: CutSSEEvent) => {
              if (event.type === 'clip-progress') {
                setAiStage({ clip: event.data.clip || 0, total: event.data.total || 0, stage: event.data.stage || '', message: event.data.message || '' })
              } else if (event.type === 'clip-done') {
                if (event.data.nsfwDetected) setAiStats(prev => ({ ...prev, blurred: prev.blurred + 1 }))
                else setAiStats(prev => ({ ...prev, clean: prev.clean + 1 }))
                setClipLogs(prev => [...prev, { clip: event.data.clip || 0, filename: event.data.filename || '', url: event.data.url || '', nsfwDetected: event.data.nsfwDetected || false, aiLog: event.data.aiLog }])
              }
            },
          )
          totalClips += data.clips.length
          resultFolders.push({ id: data.targetFolderId, name: data.targetFolderName, clipCount: data.clips.length })
        } else {
          const data = await videoCut.mutateAsync({ videoUrl: video.url, maxClips, maxDuration, folderId, clipTitle, reelsMode })
          totalClips += data.clips.length
          resultFolders.push({ id: data.targetFolderId, name: data.targetFolderName, clipCount: data.clips.length })
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido'
        failed.push(`${clipTitle}: ${msg}`)
      }
    }

    setBulkCutting(false)
    setAiStage(null)
    setBulkResult({ totalVideos: selectedVideos.length, totalClips, failed, folders: resultFolders })

    if (failed.length === 0) toast.success(`${selectedVideos.length} videos cortados! ${totalClips} clips no total`)
    else toast.error(`${failed.length} video(s) falharam`)
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      {/* Header */}
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Scissors className="h-5 w-5" /> {t('Corte de Video')}
        </h2>
        <p className="text-sm text-muted-foreground">
          Navegue pelas pastas, selecione os videos e corte em Reels
        </p>
      </div>

      {/* File Browser */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {/* Breadcrumb + actions bar */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <button
            type="button"
            onClick={() => setCurrentFolderId('')}
            className={cn(
              'flex items-center gap-1 text-xs font-medium transition-colors',
              !currentFolderId ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Home className="h-3.5 w-3.5" />
            Biblioteca
          </button>
          {breadcrumb.map((item) => (
            <div key={item.id} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
              <button
                type="button"
                onClick={() => navigateTo(item.id)}
                className={cn(
                  'text-xs font-medium transition-colors truncate max-w-[120px]',
                  item.id === currentFolderId ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {item.name}
              </button>
            </div>
          ))}

          <div className="ml-auto flex items-center gap-1.5">
            {currentFolderId && (
              <Button size="sm" variant="ghost" className="h-7 gap-1 text-[10px]" onClick={() => uploadRef.current?.click()} disabled={uploadToFolder.isPending}>
                {uploadToFolder.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                {uploadToFolder.isPending ? `${uploadProgress}%` : 'Upload'}
              </Button>
            )}
            {showCreateFolder ? (
              <div className="flex items-center gap-1">
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Nome da pasta..."
                  className="h-7 w-32 text-[10px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder()
                    if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName('') }
                  }}
                />
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCreateFolder} disabled={createFolder.isPending}>
                  <Check className="h-3 w-3 text-primary" />
                </Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setShowCreateFolder(false); setNewFolderName('') }}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="h-7 gap-1 text-[10px]" onClick={() => setShowCreateFolder(true)}>
                <Plus className="h-3 w-3" /> Nova pasta
              </Button>
            )}
          </div>
        </div>

        <input
          ref={uploadRef}
          type="file"
          multiple
          accept="video/mp4,video/quicktime,video/webm"
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) { handleUpload(e.target.files); e.target.value = '' } }}
        />

        {/* Upload progress */}
        {uploadToFolder.isPending && (
          <div className="border-b border-border bg-primary/5 px-4 py-2">
            <div className="flex items-center gap-2 text-xs text-primary">
              <Loader2 className="h-3 w-3 animate-spin" />
              Enviando {fileCount} arquivo(s)... {uploadProgress}%
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-primary/10">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Select all bar */}
        {currentVideos.length > 0 && (
          <div className="flex items-center gap-3 border-b border-border/50 bg-muted/30 px-4 py-1.5">
            <Checkbox
              checked={currentVideos.length > 0 && currentVideos.every(v => selectedVideoIds.has(v.id))}
              onCheckedChange={toggleAllCurrentVideos}
              className="h-3.5 w-3.5"
            />
            <span className="text-[10px] text-muted-foreground">
              {currentVideos.filter(v => selectedVideoIds.has(v.id)).length} de {currentVideos.length} selecionado(s) nesta pasta
            </span>
            {selectedVideoIds.size > 0 && selectedVideoIds.size !== currentVideos.filter(v => selectedVideoIds.has(v.id)).length && (
              <span className="text-[10px] text-primary font-medium">
                ({selectedVideoIds.size} total selecionado{selectedVideoIds.size !== 1 ? 's' : ''})
              </span>
            )}
          </div>
        )}

        {/* Content grid */}
        <div className="max-h-[400px] overflow-y-auto">
          {subfolders.length === 0 && currentVideos.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <FolderOpen className="h-10 w-10 text-muted-foreground/15" />
              <p className="text-xs text-muted-foreground">
                {!currentFolderId ? 'Nenhuma pasta criada' : 'Pasta vazia'}
              </p>
              {currentFolderId && (
                <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => uploadRef.current?.click()}>
                  <Upload className="h-3 w-3" /> Subir Videos
                </Button>
              )}
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {/* Subfolders */}
              {subfolders.map(folder => {
                const vidCount = countVideosRecursive(folder.id)
                return (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => navigateTo(folder.id)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <Folder className="h-5 w-5 text-blue-500 shrink-0" />
                    <span className="flex-1 min-w-0 text-sm font-medium truncate">{folder.name}</span>
                    {vidCount > 0 && (
                      <span className="text-[10px] text-muted-foreground shrink-0">{vidCount} video{vidCount !== 1 ? 's' : ''}</span>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                  </button>
                )
              })}

              {/* Videos */}
              {currentVideos.length > 0 && subfolders.length > 0 && (
                <div className="border-t border-border/30 my-1" />
              )}
              {currentVideos.map(v => {
                const isSelected = selectedVideoIds.has(v.id)
                return (
                  <div
                    key={v.id}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleVideo(v.id)}
                      className="h-4 w-4 shrink-0"
                    />

                    {/* Thumbnail */}
                    <div
                      className="relative h-12 w-20 shrink-0 overflow-hidden rounded-md bg-black cursor-pointer"
                      onClick={() => setPlayingId(playingId === v.id ? null : v.id)}
                    >
                      {playingId === v.id ? (
                        <video src={v.url} className="h-full w-full object-cover" controls autoPlay onEnded={() => setPlayingId(null)} />
                      ) : (
                        <>
                          <video src={v.url} className="h-full w-full object-cover" muted preload="metadata" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <Play className="h-3.5 w-3.5 fill-white text-white" />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Editable name */}
                    <input
                      type="text"
                      value={videoNames[v.id] ?? v.filename.replace(/\.[^.]+$/, '')}
                      onChange={(e) => setVideoNames(prev => ({ ...prev, [v.id]: e.target.value }))}
                      className="flex-1 min-w-0 bg-transparent text-xs text-foreground outline-none border-b border-transparent hover:border-border focus:border-primary py-1"
                    />

                    <Video className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {selectedVideoIds.size > 0 && (
          <div className="flex items-center justify-between border-t border-border bg-primary/5 px-4 py-2">
            <span className="text-xs font-medium text-primary">
              {selectedVideoIds.size} video{selectedVideoIds.size !== 1 ? 's' : ''} selecionado{selectedVideoIds.size !== 1 ? 's' : ''}
            </span>
            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-muted-foreground" onClick={() => setSelectedVideoIds(new Set())}>
              Limpar selecao
            </Button>
          </div>
        )}
      </div>

      {/* Cut config — only show when videos are selected */}
      {selectedVideoIds.size > 0 && (
        <div className="space-y-4 rounded-lg border border-border p-4">
          <div className="flex items-center gap-2">
            <Scissors className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{t('Configurar Corte')}</span>
            <Badge className="text-[9px]">{selectedVideoIds.size} video{selectedVideoIds.size !== 1 ? 's' : ''}</Badge>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-xs font-medium">{t('Modo Reels')}</p>
              <p className="text-[10px] text-muted-foreground">Facebook: 3-90s | Instagram: 3-180s</p>
            </div>
            <Switch checked={reelsMode} onCheckedChange={(v) => { setReelsMode(v); if (v && maxDuration > 90) setMaxDuration(90) }} />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium">{t('Revisar Conteudo')}</p>
                <Badge variant="outline" className="text-[8px] px-1.5 py-0 h-4 border-yellow-500/50 text-yellow-600">BETA</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground">{t('Detecta e borra conteudo sensivel automaticamente')}</p>
              <p className="text-[9px] text-muted-foreground/60">+5-15s {t('por clip')} (CPU)</p>
            </div>
            <Switch checked={nsfwReview} onCheckedChange={setNsfwReview} disabled={bulkCutting} />
          </div>

          {nsfwReview && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-xs font-medium">{t('Modelo de IA')}</p>
                <p className="text-[10px] text-muted-foreground">
                  {clipModel === 'openclip' ? 'CLIP generico (padrao)' : 'Modelo treinado customizado'}
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                {(['openclip', 'dinov2'] as const).map(m => (
                  <button key={m} type="button" onClick={() => setClipModel(m)} disabled={bulkCutting}
                    className={cn('rounded-md px-3 py-1 text-xs font-medium transition-colors', clipModel === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                    {m === 'openclip' ? 'OpenCLIP' : 'DINOv2'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {nsfwReview && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-xs font-medium">{t('Qualidade de Saida')}</p>
                <p className="text-[10px] text-muted-foreground">
                  {outputQuality === '1080p' ? 'Maior qualidade, +15-20s por clip' : 'Recomendado, mais rapido'}
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                {(['720p', '1080p'] as const).map(q => (
                  <button key={q} type="button" onClick={() => setOutputQuality(q)} disabled={bulkCutting}
                    className={cn('rounded-md px-3 py-1 text-xs font-medium transition-colors', outputQuality === q ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {nsfwReview && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium mb-1.5">{t('Sensibilidade')}</p>
                <p className="text-[9px] text-muted-foreground mb-2">
                  {aiSensitivity === 'low' && 'Detecta apenas conteudo explicito'}
                  {aiSensitivity === 'medium' && 'Equilibrado (recomendado)'}
                  {aiSensitivity === 'high' && 'Detecta conteudo sugestivo leve'}
                </p>
                <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                  {(['low', 'medium', 'high'] as const).map((v) => (
                    <button key={v} type="button" onClick={() => setAiSensitivity(v)} disabled={bulkCutting}
                      className={cn('flex-1 rounded-md px-2 py-1 text-[10px] font-medium transition-colors', aiSensitivity === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                      {v === 'low' ? 'Baixa' : v === 'medium' ? 'Media' : 'Alta'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium mb-1.5">{t('Intensidade do Blur')}</p>
                <p className="text-[9px] text-muted-foreground mb-2">
                  {blurIntensity === 'light' && 'Leve, menos perceptivel'}
                  {blurIntensity === 'medium' && 'Moderado (recomendado)'}
                  {blurIntensity === 'strong' && 'Forte, cobre completamente'}
                </p>
                <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                  {(['light', 'medium', 'strong'] as const).map((v) => (
                    <button key={v} type="button" onClick={() => setBlurIntensity(v)} disabled={bulkCutting}
                      className={cn('flex-1 rounded-md px-2 py-1 text-[10px] font-medium transition-colors', blurIntensity === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                      {v === 'light' ? 'Leve' : v === 'medium' ? 'Medio' : 'Forte'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium">{t('Max cortes por video')}</label>
              <Input
                type="number" min={1} max={20} value={maxClips || ''}
                onChange={(e) => setMaxClips(Math.min(20, parseInt(e.target.value) || 0))}
                onBlur={() => setMaxClips(v => Math.max(1, v))}
                className="mt-1 h-9 text-sm" disabled={bulkCutting}
              />
            </div>
            <div>
              <label className="text-xs font-medium">
                {t('Duracao max')}: <span className="text-primary">{maxDuration}s</span>
                {reelsMode && <span className="ml-1 text-[10px] text-muted-foreground">(Reels)</span>}
              </label>
              <Slider
                min={reelsMode ? 3 : 10} max={reelsMode ? 90 : 240} step={reelsMode ? 1 : 5}
                value={[maxDuration]} onValueChange={([v]) => setMaxDuration(v)}
                disabled={bulkCutting} className="mt-3"
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{reelsMode ? '3s' : '10s'}</span>
                <span>{reelsMode ? '90s' : '240s'}</span>
              </div>
            </div>
          </div>

          {/* Bulk cutting progress */}
          {bulkCutting && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary">
                    {t('Video')} {bulkProgress.current}/{bulkProgress.total}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{bulkProgress.currentName}</p>
                </div>
              </div>

              {nsfwReview && aiStage && (
                <div className="rounded-md border border-border bg-card p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    {aiStage.stage === 'cutting' && <Scissors className="h-3.5 w-3.5 text-blue-500" />}
                    {aiStage.stage === 'analyzing' && <Eye className="h-3.5 w-3.5 text-yellow-500" />}
                    {aiStage.stage === 'blurring' && <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />}
                    {aiStage.stage === 'compressing' && <Video className="h-3.5 w-3.5 text-muted-foreground" />}
                    {aiStage.stage === 'uploading' && <Upload className="h-3.5 w-3.5 text-emerald-500" />}
                    {aiStage.stage === 'downloading' && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    <span className="text-xs font-medium">{aiStage.message}</span>
                  </div>
                  {aiStage.total > 0 && (
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${(aiStage.clip / aiStage.total) * 100}%` }} />
                    </div>
                  )}
                  {(aiStats.blurred > 0 || aiStats.clean > 0) && (
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      {aiStats.clean > 0 && <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> {aiStats.clean} {t('limpos')}</span>}
                      {aiStats.blurred > 0 && <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-yellow-500" /> {aiStats.blurred} {t('borrados')}</span>}
                    </div>
                  )}
                </div>
              )}

              <div className="h-2 w-full overflow-hidden rounded-full bg-primary/10">
                <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Bulk result */}
          {bulkResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-lg border border-green-500/20 bg-green-500/5 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    {bulkResult.totalVideos - bulkResult.failed.length} video(s) cortados! {bulkResult.totalClips} clips no total
                  </p>
                  <p className="text-[11px] text-muted-foreground">{bulkResult.folders.length} subpastas criadas</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setShowSchedule(true)}>
                  <CalendarPlus className="h-3.5 w-3.5" /> Agendar Cortes
                </Button>
              </div>

              {bulkResult.failed.length > 0 && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <p className="text-xs font-medium text-destructive">{bulkResult.failed.length} falha(s)</p>
                  </div>
                  {bulkResult.failed.map((msg, i) => (
                    <p key={i} className="text-[10px] text-muted-foreground">{msg}</p>
                  ))}
                </div>
              )}

              {clipLogs.length > 0 && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-muted-foreground" />
                    <p className="text-xs font-medium">Log da IA — {clipLogs.length} clip(s)</p>
                  </div>
                  <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                    {clipLogs.map((log, idx) => (
                      <div key={idx} className="rounded-md border border-border overflow-hidden">
                        <button type="button" className="flex w-full items-center gap-2 p-2 text-left hover:bg-muted/50 transition-colors" onClick={() => setExpandedLog(expandedLog === idx ? null : idx)}>
                          <ChevronDown className={cn('h-3 w-3 text-muted-foreground shrink-0 transition-transform', expandedLog === idx && 'rotate-180')} />
                          <span className="text-[10px] font-medium truncate flex-1">Clip {log.clip} — {log.filename}</span>
                          <Badge variant="outline" className={cn('text-[8px] px-1.5 py-0 h-4 shrink-0', log.nsfwDetected ? 'border-yellow-500/50 text-yellow-600' : 'border-emerald-500/50 text-emerald-600')}>
                            {log.nsfwDetected ? 'BLUR' : 'LIMPO'}
                          </Badge>
                        </button>
                        {expandedLog === idx && (
                          <div className="border-t border-border p-2 space-y-2 bg-muted/30">
                            {log.url && <video src={log.url} className="w-full max-h-[200px] rounded-md bg-black object-contain" controls preload="metadata" />}
                            {log.aiLog ? <AiLogDetail aiLog={log.aiLog} clipFilename={log.filename} /> : <p className="text-[10px] text-muted-foreground">Sem dados de analise</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <Button className="w-full gap-2" onClick={handleBulkCut} disabled={bulkCutting || selectedVideoIds.size === 0}>
            <Scissors className="h-4 w-4" />
            Cortar {selectedVideoIds.size} Video{selectedVideoIds.size !== 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {bulkResult && bulkResult.folders.length > 0 && (
        <QuickScheduleDialog open={showSchedule} onOpenChange={setShowSchedule} folders={bulkResult.folders} cutMode />
      )}
    </div>
  )
}
